import React, { type FC, type ReactNode, useState } from 'react'
import classnames from 'classnames'
import { AvatarGroup, type IAvatarInfo, Typography, Tooltip, Icon } from 'shared/ui'
import { getLabelAsNumberInternationalFormat, isNumeric } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { useCopyToClipboard } from 'shared/hooks/useCopyToClipboard'
import styles from 'widgets/CallPopUp/ui/styles.module.scss'
import { HoldStatus } from 'widgets/CallPopUp/ui/components/BaseContent/HoldStatus/HoldStatus'
import { useCallPopUpDefaultContext } from 'widgets/CallPopUp/context/callPopUpDefaultContext'

type IBaseContentProps = {
  withPadding?: boolean
  avatarsInfo: IAvatarInfo[]
  name: string
  number?: string
  desc: string
  isHold?: boolean
  actionsTop?: ReactNode
  actionsBottom?: ReactNode
  content?: ReactNode
}

export const BaseContent: FC<IBaseContentProps> = ({
  withPadding,
  avatarsInfo,
  isHold,
  name,
  number,
  desc,
  actionsTop,
  actionsBottom,
  content,
}) => {
  const [showEllipsis, setShowEllipsis] = useState(false)
  const [open, setOpen] = useState(false)
  const { onCopy, copied } = useCopyToClipboard(number)
  const store = useCallPopUpDefaultContext()
  const { isHover, callPopUpPowerDialerStoreV2 } = store
  const hasNumber = name.startsWith('+') && isNumeric(name)

  return (
    <div
      className={classnames(styles.content, {
        [styles.withoutPadding]: withPadding,
      })}
    >
      <div
        className={classnames(styles.content__section, styles['content__section--info'], {
          [styles.withPadding]: withPadding,
        })}
      >
        <div className={styles.content__box}>
          <div className={styles.info}>
            <div className={styles.info__avatar}>
              <AvatarGroup items={avatarsInfo} size={40} />
            </div>
            <div className={styles.info__box}>
              <div
                className={classnames(styles.info__label, {
                  [styles.withRightPadding]: !!number,
                })}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
              >
                <Typography
                  ellipsis
                  tag={'div'}
                  ariaLabel='callPopUpName'
                  className={styles.info__title}
                  tooltipProps={{ label: name, zIndex: ZIndex.OVERLAY_TOP_1 }}
                  onShowEllipsis={setShowEllipsis}
                >
                  {name && getLabelAsNumberInternationalFormat(name)}
                </Typography>
                {number && (
                  <Tooltip
                    placement='top'
                    padding={4}
                    label={
                      <>
                        {!hasNumber && showEllipsis && (
                          <Typography
                            tag={'div'}
                            ariaLabel='callPopUpTooltipTitle'
                            variant={'body-sm-regular'}
                            color={'var(--content-primary-tertiary-inverted)'}
                            className={styles.info__tooltipTitle}
                          >
                            {name}
                          </Typography>
                        )}
                        <button className={styles.info__button} onClick={onCopy}>
                          <div className={styles.info__button__box}>
                            <Icon
                              icon='copy'
                              fontSize={12}
                              color={'var(--content-primary-primary-inverted)'}
                            />
                            <Typography
                              tag={'div'}
                              ariaLabel='callPopUpNumber'
                              variant={'body-md-regular'}
                              color={'var(--content-primary-primary-inverted)'}
                              className={styles.info__number}
                            >
                              {copied ? 'Copied' : number}
                            </Typography>
                          </div>
                        </button>
                      </>
                    }
                    zIndex={ZIndex.OVERLAY_TOP_1}
                    open={open}
                  >
                    <Icon
                      className={classnames(styles.info__icon, {
                        [styles.visible]: isHover,
                      })}
                      icon='infoCircle'
                      fontSize={12}
                      color={'var(--content-neutral-primary)'}
                    />
                  </Tooltip>
                )}
              </div>
              {isHold && <HoldStatus />}
              {!isHold && (
                <div className={styles.info__titleSmall}>
                  {desc && getLabelAsNumberInternationalFormat(desc)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.content__actions}>{actionsTop}</div>
      </div>

      {callPopUpPowerDialerStoreV2.isShowQuietHoursContent && (
        <div className={classnames(styles.content, styles.quietHoursWrap)}>
          <div className={styles.quietHours}>
            <Icon icon='moon' fontSize={16} color={'var(--deep-purple-70)'} />
            <Typography
              variant={'body-md-regular'}
              ariaLabel={'QuietHoursContactInfo'}
              color={'var(--deep-purple-80)'}
            >
              It’s {callPopUpPowerDialerStoreV2.localContactTime} for{' '}
              {callPopUpPowerDialerStoreV2.contactQuietHoursName}
            </Typography>
          </div>
        </div>
      )}

      <div
        className={classnames(styles.content__section, {
          [styles.withPadding]: withPadding,
        })}
      >
        {actionsBottom}
      </div>

      {content}
    </div>
  )
}
